import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { ProjectInsights, CharacterIntel, LocationIntel, ThreadIntel, CharacterArc, Manuscript } from "../lib/types.js";
import type { SidebarSection } from "../workspace/Sidebar.js";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { ConfirmDialog } from "../components/ConfirmDialog.js";
import { ContinuityCard } from "./ContinuityCard.js";
import { KnowledgeHealthCard } from "./KnowledgeHealthCard.js";
import { PolishCard } from "./PolishCard.js";
import { IntelDashboard } from "./IntelDashboard.js";
import { BookOverviewCard } from "./BookOverviewCard.js";
import { LedgerPanel } from "./LedgerPanel.js";
import { NeedsReviewPanel } from "./NeedsReviewPanel.js";
import { RevisionPanel } from "./RevisionPanel.js";
import { PromisesPanel } from "./PromisesPanel.js";
import { AskBookPanel } from "./AskBookPanel.js";
import { CanonPanel } from "./CanonPanel.js";
import { PacingPanel } from "./PacingPanel.js";
import { DraftsPanel } from "./DraftsPanel.js";
import { BetaReaderPanel } from "./BetaReaderPanel.js";
import { formatNumber } from "../lib/format.js";
import { useModelPicker } from "../ai/ModelPicker.js";

interface Props {
  projectId: string;
  manuscript: Manuscript;
  onOpenScene: (sceneId: string) => void;
  onOpenSection?: (section: SidebarSection) => void;
  /** Notify the workspace that auto-populate state changed (for the sidebar indicator). */
  onIntelChanged?: () => void;
}

type IntelTab = "overview" | "ask" | "characters" | "locations" | "plots" | "promises" | "canon" | "ledger" | "pacing" | "summaries" | "continuity" | "health" | "polish" | "revision" | "drafts" | "beta" | "review";

const TAB_DEFS: [IntelTab, string, IconName][] = [
  ["overview", "Overview", "home"],
  ["ask", "Ask the Book", "search"],
  ["characters", "Characters", "users"],
  ["locations", "Locations", "map"],
  ["plots", "Plot Threads", "thread"],
  ["promises", "Story Promises", "target"],
  ["canon", "Canon", "check"],
  ["ledger", "Event Ledger", "timeline"],
  ["pacing", "Pacing", "history"],
  ["summaries", "Scene Summaries", "file"],
  ["continuity", "Continuity", "alert"],
  ["health", "Knowledge Health", "heart"],
  ["polish", "Prose Polish", "sparkles"],
  ["revision", "Revision", "edit"],
  ["drafts", "Drafts", "layers"],
  ["beta", "Beta Reader", "users"],
  ["review", "Needs Review", "info"]
];

export function InsightsPanel({ projectId, manuscript, onOpenScene, onOpenSection, onIntelChanged }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [insights, setInsights] = useState<ProjectInsights | null>(null);
  const [loading, setLoading] = useState(true);
  const [reindexing, setReindexing] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [confirmExtract, setConfirmExtract] = useState(false);
  const [progress, setProgress] = useState<{ doneChunks: number; totalChunks: number; doneWords: number; totalWords: number } | null>(null);
  const [engine, setEngine] = useState<{ provider: string | null; model: string | null } | null>(null);
  const [tab, setTab] = useState<IntelTab>("overview");
  const [warning, setWarning] = useState<string | null>(null);
  const [expandedSummaries, setExpandedSummaries] = useState<Set<string>>(new Set());
  // Derived scene-sourced intelligence (importance-ranked, hierarchy, health).
  const [charIntel, setCharIntel] = useState<CharacterIntel[]>([]);
  const [locIntel, setLocIntel] = useState<LocationIntel[]>([]);
  const [threadIntel, setThreadIntel] = useState<ThreadIntel[]>([]);
  const [reviewOpen, setReviewOpen] = useState(0);
  const [dashKey, setDashKey] = useState(0);

  const loadDerived = useCallback(async () => {
    try {
      const [c, l, t, r] = await Promise.all([
        api.intelCharacters(projectId), api.intelLocations(projectId),
        api.intelThreads(projectId), api.reviewQueue(projectId, "open")
      ]);
      setCharIntel(c.characters); setLocIntel(l.locations); setThreadIntel(t.threads); setReviewOpen(r.openCount);
    } catch { /* non-fatal */ }
  }, [projectId]);
  useEffect(() => { void loadDerived(); }, [loadDerived]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setInsights(await api.insights(projectId));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load insights");
    } finally {
      setLoading(false);
    }
  }, [projectId, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const reindex = async () => {
    setReindexing(true);
    try {
      const { insights: fresh } = await api.reindex(projectId);
      setInsights(fresh);
      void loadDerived();
      setDashKey((n) => n + 1);
      toast.success("Manuscript reindexed.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Reindex failed");
    } finally {
      setReindexing(false);
    }
  };

  const summarizeMissing = async () => {
    const choice = await pickModel({ task: "summary" });
    if (!choice) return;
    setSummarizing(true);
    try {
      const { result } = await api.summarizeMissing(projectId, choice);
      toast.success(`Summarized ${result.summarized} scene${result.summarized === 1 ? "" : "s"}.${result.remaining ? ` ${result.remaining} remaining.` : ""}`);
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Summarize failed");
    } finally {
      setSummarizing(false);
    }
  };

  /** Poll the background analysis until it finishes, then show what it found. */
  const watchExtraction = useCallback(async () => {
    setExtracting(true);
    try {
      for (;;) {
        const { job, provider, model, insights: fresh } = await api.extractionStatus(projectId);
        if (!job) return;
        setProgress({ doneChunks: job.doneChunks, totalChunks: job.totalChunks, doneWords: job.doneWords, totalWords: job.totalWords });
        setEngine({ provider, model });
        if (job.status === "running") {
          await new Promise((r) => setTimeout(r, 2000));
          continue;
        }
        if (job.status === "error") {
          toast.error(job.error ?? "Analysis failed");
          return;
        }
        if (fresh) setInsights(fresh);
        void loadDerived();
        setDashKey((n) => n + 1);
        const created = job.createdCounts;
        const updated = job.updatedCounts;
        const createdTotal = created ? Object.values(created).reduce((a, b) => a + b, 0) : 0;
        const updatedTotal = updated ? Object.values(updated).reduce((a, b) => a + b, 0) : 0;
        const reused = job.cachedChunks ?? 0;
        const reuseNote = reused > 0 ? ` ${reused} of ${job.totalChunks} section${job.totalChunks === 1 ? "" : "s"} reused from cache.` : "";
        if (createdTotal === 0 && updatedTotal === 0) {
          toast.info(`Scribe found nothing new to add (or no AI provider is configured).${reuseNote}`);
        } else if (created) {
          toast.success(
            `Added ${created.characters} characters, ${created.locations} locations, ${created.storyBible} bible entries, ${created.timeline} events, ${created.plotThreads} threads` +
              (updatedTotal ? ` · enriched ${updatedTotal} existing.` : ".") + reuseNote
          );
        }
        // Be upfront when part of the book could not be read.
        if (job.failedChunks > 0) {
          setWarning(
            `${job.failedChunks} of ${job.totalChunks} section${job.totalChunks === 1 ? "" : "s"} of the book couldn't be read, so some characters or details may be missing — run Auto-populate again to retry just those sections.`
          );
        }
        return;
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Analysis failed");
    } finally {
      setExtracting(false);
      setProgress(null);
      onIntelChanged?.();
    }
  }, [projectId, toast, onIntelChanged]);

  const runExtract = async () => {
    setConfirmExtract(false);
    const choice = await pickModel({ task: "extraction" });
    if (!choice) return;
    setWarning(null);
    try {
      await api.startExtraction(projectId, choice);
      onIntelChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Analysis failed");
      return;
    }
    await watchExtraction();
  };

  // If the page is opened (or refreshed) while an analysis is running, pick it back up.
  useEffect(() => {
    let cancelled = false;
    api.extractionStatus(projectId).then(({ job, provider, model }) => {
      if (cancelled) return;
      setEngine({ provider, model });
      if (job?.status === "running") void watchExtraction();
    }).catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [projectId, watchExtraction]);

  return (
    <div className="insights">
      <header className="insights-head">
        <div>
          <h2 style={{ fontSize: 18 }}>Manuscript Intelligence</h2>
          <p className="faint" style={{ fontSize: 12, margin: "2px 0 0" }}>
            Cross-references, mentions, plot-thread health, and scene summaries.
          </p>
        </div>
        <div className="row gap-2">
          <button className="btn btn-sm" onClick={reindex} disabled={reindexing}>
            <Icon name={reindexing ? "clock" : "history"} size={14} className={reindexing ? "spin" : ""} /> Refresh Analysis
          </button>
          <button className="btn btn-sm" onClick={summarizeMissing} disabled={summarizing}>
            <Icon name={summarizing ? "clock" : "file"} size={14} className={summarizing ? "spin" : ""} /> Summarize Scenes
          </button>
          <button className="btn btn-sm btn-primary" onClick={() => setConfirmExtract(true)} disabled={extracting}>
            <Icon name={extracting ? "clock" : "sparkles"} size={14} className={extracting ? "spin" : ""} />
            {extracting ? "Analyzing book…" : "Auto-Populate from Book"}
          </button>
        </div>
      </header>

      {extracting && progress && progress.totalChunks > 0 && (
        <div className="insights-hint card" style={{ marginTop: 10 }}>
          <Icon name="sparkles" size={16} className="spin" style={{ color: "var(--accent)" }} />
          <span>
            {progress.doneChunks} / {progress.totalChunks} chunks complete · {formatNumber(progress.doneWords)} / {formatNumber(progress.totalWords)} words analyzed
            {engine?.provider && (
              <> · {engine.provider}{engine.model ? ` (${engine.model})` : ""}</>
            )}
          </span>
        </div>
      )}

      {loading || !insights ? (
        <div className="insights-loading"><Icon name="sparkles" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>
      ) : (
        <div className="insights-body">
          {warning && (
            <div className="insights-hint card">
              <Icon name="alert" size={16} style={{ color: "var(--warning)" }} />
              <span>{warning}</span>
            </div>
          )}
          {!insights.hasIndex && (
            <div className="insights-hint card">
              <Icon name="info" size={16} style={{ color: "var(--info)" }} />
              <span>Run <strong>Reindex</strong> to scan the manuscript for character and location mentions.</span>
            </div>
          )}

          <div className="insights-tabs" role="tablist">
            {TAB_DEFS.map(([key, label, icon]) => (
              <button
                key={key}
                role="tab"
                aria-selected={tab === key}
                className={`insights-tab ${tab === key ? "active" : ""}`}
                onClick={() => setTab(key)}
                title={label}
              >
                <Icon name={icon} size={16} className="insights-tab-ico" />
                <span className="insights-tab-label">{label}</span>
                {key === "review" && reviewOpen > 0 && <span className="insights-tab-badge">{reviewOpen}</span>}
              </button>
            ))}
          </div>

          {tab === "overview" && (
            <>
              <BookOverviewCard projectId={projectId} reloadKey={dashKey} />
              <IntelDashboard
                projectId={projectId}
                manuscript={manuscript}
                reloadKey={dashKey}
                onOpenTab={(t) => {
                  if (t === "relationships" || t === "timeline") onOpenSection?.(t as SidebarSection);
                  else setTab(t as IntelTab);
                }}
              />
            </>
          )}
          {tab === "ask" && <AskBookPanel projectId={projectId} onOpenScene={onOpenScene} />}
          {tab === "promises" && <PromisesPanel projectId={projectId} onOpenScene={onOpenScene} onChanged={() => setDashKey((n) => n + 1)} />}
          {tab === "canon" && <CanonPanel projectId={projectId} onChanged={() => { void loadDerived(); setDashKey((n) => n + 1); }} />}
          {tab === "ledger" && <LedgerPanel projectId={projectId} onOpenScene={onOpenScene} />}
          {tab === "pacing" && <PacingPanel projectId={projectId} onOpenScene={onOpenScene} />}
          {tab === "revision" && <RevisionPanel projectId={projectId} onOpenScene={onOpenScene} />}
          {tab === "drafts" && <DraftsPanel projectId={projectId} onOpenScene={onOpenScene} />}
          {tab === "beta" && <BetaReaderPanel projectId={projectId} />}
          {tab === "review" && <NeedsReviewPanel projectId={projectId} onChanged={() => { void loadDerived(); setDashKey((n) => n + 1); }} />}
          {tab === "continuity" && <ContinuityCard projectId={projectId} onOpenScene={onOpenScene} />}
          {tab === "health" && <KnowledgeHealthCard projectId={projectId} defaultOpen />}
          {tab === "polish" && <PolishCard projectId={projectId} onOpenScene={onOpenScene} />}

          {tab === "characters" && <CharactersTab projectId={projectId} chars={charIntel} onOpenScene={onOpenScene} />}
          {tab === "locations" && <LocationsTab locs={locIntel} />}
          {tab === "plots" && <PlotsTab threads={threadIntel} />}

          {tab === "summaries" && (
          <div className="insights-grid insights-grid-single">
            {/* Scene summaries — expandable list of every scene */}
            <section className="card insight-card insight-card-wide">
              <div className="insight-card-head"><Icon name="file" size={16} style={{ color: "var(--accent)" }} /><h3>Scene summaries</h3></div>
              <div className="progress-track" style={{ margin: "4px 0 8px" }}>
                <div className="progress-fill" style={{ width: `${insights.summaries.total ? (insights.summaries.withSummary / insights.summaries.total) * 100 : 0}%` }} />
              </div>
              <p className="muted" style={{ fontSize: 12, margin: "0 0 10px" }}>
                {insights.summaries.withSummary} of {insights.summaries.total} scenes summarized.
              </p>
              {insights.summaries.scenes.length === 0 ? (
                <p className="muted insight-empty">No scenes yet.</p>
              ) : (
                <ul className="summary-list">
                  {insights.summaries.scenes.map((s) => (
                    <SummaryRow
                      key={s.sceneId}
                      scene={s}
                      expanded={expandedSummaries.has(s.sceneId)}
                      onToggle={() =>
                        setExpandedSummaries((prev) => {
                          const next = new Set(prev);
                          next.has(s.sceneId) ? next.delete(s.sceneId) : next.add(s.sceneId);
                          return next;
                        })
                      }
                      onOpenScene={onOpenScene}
                      onSummarized={load}
                    />
                  ))}
                </ul>
              )}
            </section>
          </div>
          )}
        </div>
      )}

      {confirmExtract && (
        <ConfirmDialog
          title="Auto-populate from your manuscript"
          message="Scribe reads your manuscript and creates Characters, Locations, Story Bible entries, Timeline events, and Plot Threads it finds. Re-running is cheap: sections that haven't changed since last time are reused from cache for free, so only new or edited sections are re-analyzed. It only fills in empty fields on entries you already have — it never overwrites your work — and it never changes your manuscript. This uses your AI provider; the first full run can take a while for a long book."
          confirmLabel="Analyze & populate"
          onConfirm={runExtract}
          onCancel={() => setConfirmExtract(false)}
        />
      )}
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = { main: "Main characters", supporting: "Supporting", minor: "Minor / one-scene" };

function CharactersTab({ projectId, chars, onOpenScene }: { projectId: string; chars: CharacterIntel[]; onOpenScene: (id: string) => void }) {
  if (chars.length === 0) return <div className="insights-grid insights-grid-single"><section className="card insight-card"><p className="muted insight-empty">No characters yet. Run Auto-populate to detect them.</p></section></div>;
  const groups: Array<["main" | "supporting" | "minor", CharacterIntel[]]> = [
    ["main", chars.filter((c) => c.role === "main")],
    ["supporting", chars.filter((c) => c.role === "supporting")],
    ["minor", chars.filter((c) => c.role === "minor")]
  ];
  return (
    <div className="intel-scroll">
      {groups.filter(([, list]) => list.length > 0).map(([role, list]) => (
        <section key={role} className="card insight-card">
          <div className="insight-card-head"><Icon name="users" size={16} style={{ color: "var(--accent)" }} /><h3>{ROLE_LABEL[role]}</h3><span className="chip">{list.length}</span></div>
          <div className="intel-entities">
            {list.map((c) => (
              <div key={c.id} className="intel-entity">
                <div className="intel-entity-head">
                  <span className="strong">{c.name}</span>
                  {c.aliases && <span className="faint intel-aliases">aka {c.aliases}</span>}
                  <span className="spacer" />
                  <span className="chip">{c.sceneCount} scene{c.sceneCount === 1 ? "" : "s"}</span>
                </div>
                <div className="intel-entity-meta faint">
                  {c.chapterRange && <span>{c.chapterRange.first === c.chapterRange.last ? c.chapterRange.first : `${c.chapterRange.first} → ${c.chapterRange.last}`}</span>}
                  {c.firstAppearance && <button className="insight-link" onClick={() => onOpenScene(c.firstAppearance!.sceneId)}>first: {c.firstAppearance.title}</button>}
                  {c.lastAppearance && c.lastAppearance.sceneId !== c.firstAppearance?.sceneId && <button className="insight-link" onClick={() => onOpenScene(c.lastAppearance!.sceneId)}>last: {c.lastAppearance.title}</button>}
                </div>
                {c.relationships.length > 0 && (
                  <div className="intel-chiprow">
                    {c.relationships.slice(0, 6).map((r, i) => <span key={i} className="chip intel-rel">{r.name}{r.status ? ` · ${r.status}` : ""}</span>)}
                  </div>
                )}
                {c.plotThreads.length > 0 && (
                  <div className="intel-chiprow">
                    {c.plotThreads.slice(0, 5).map((t) => <span key={t.id} className="chip">{t.name}</span>)}
                  </div>
                )}
                <ArcDetail projectId={projectId} characterId={c.id} onOpenScene={onOpenScene} />
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

function ArcDetail({ projectId, characterId, onOpenScene }: { projectId: string; characterId: string; onOpenScene: (id: string) => void }) {
  const [open, setOpen] = useState(false);
  const [arc, setArc] = useState<CharacterArc | null>(null);
  const [loading, setLoading] = useState(false);
  const toggle = async () => {
    const next = !open;
    setOpen(next);
    if (next && !arc) {
      setLoading(true);
      try { setArc(await api.characterArc(projectId, characterId)); } catch { /* ignore */ } finally { setLoading(false); }
    }
  };
  return (
    <div className="arc">
      <button className="arc-toggle" onClick={() => void toggle()}>
        <Icon name={open ? "chevronDown" : "chevronRight"} size={12} /> Arc
      </button>
      {open && (
        loading ? <div className="faint" style={{ fontSize: 12, padding: "4px 0" }}>Loading…</div> :
        !arc || arc.beats.length === 0 ? <div className="faint" style={{ fontSize: 12, padding: "4px 0" }}>No arc beats yet — add relationships or run "Analyze scene" to capture changes.</div> :
        <ol className="arc-beats">
          {arc.beats.map((b, i) => (
            <li key={i} className={`arc-beat arc-beat--${b.kind}`}>
              <button className="insight-link" onClick={() => onOpenScene(b.sceneId)}>{b.sceneLabel}</button>
              <span className="arc-beat-text">{b.text}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function LocationsTab({ locs }: { locs: LocationIntel[] }) {
  if (locs.length === 0) return <div className="insights-grid insights-grid-single"><section className="card insight-card"><p className="muted insight-empty">No locations yet.</p></section></div>;
  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head"><Icon name="map" size={16} style={{ color: "var(--accent)" }} /><h3>Locations</h3><span className="chip">{locs.length} primary</span></div>
        <div className="intel-entities">
          {locs.map((l) => (
            <div key={l.id} className="intel-entity">
              <div className="intel-entity-head">
                <span className="strong">{l.name}</span>
                {l.type && l.type !== "primary" && <span className="chip intel-type">{l.type}</span>}
                <span className="spacer" />
                <span className="chip">{l.sceneCount} scene{l.sceneCount === 1 ? "" : "s"}</span>
              </div>
              {l.aliases && <div className="faint intel-aliases">aka {l.aliases}</div>}
              {l.children.length > 0 && (
                <ul className="intel-children">
                  {l.children.map((ch) => (
                    <li key={ch.id} className="intel-child">
                      <Icon name="chevronRight" size={12} className="faint" />
                      <span>{ch.name}</span>
                      <span className="spacer" />
                      <span className="faint" style={{ fontSize: 11 }}>{ch.sceneCount} scene{ch.sceneCount === 1 ? "" : "s"}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

const HEALTH_META: Record<string, { label: string; cls: string }> = {
  active: { label: "Active", cls: "insight-ok" },
  developing: { label: "Developing", cls: "insight-ok" },
  dormant: { label: "Dormant", cls: "" },
  resolved: { label: "Resolved", cls: "" },
  planned: { label: "Planned", cls: "" },
  "needs-attention": { label: "Needs attention", cls: "insight-warn" },
  "possibly-forgotten": { label: "Possibly forgotten", cls: "insight-warn" }
};

function PlotsTab({ threads }: { threads: ThreadIntel[] }) {
  if (threads.length === 0) return <div className="insights-grid insights-grid-single"><section className="card insight-card"><p className="muted insight-empty">No plot threads yet.</p></section></div>;
  const order = ["possibly-forgotten", "needs-attention", "active", "developing", "dormant", "planned", "resolved"];
  const sorted = [...threads].sort((a, b) => order.indexOf(a.health) - order.indexOf(b.health));
  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head"><Icon name="thread" size={16} style={{ color: "var(--accent)" }} /><h3>Plot threads</h3><span className="chip">{threads.length}</span></div>
        <div className="intel-entities">
          {sorted.map((t) => {
            const h = HEALTH_META[t.health] ?? { label: t.health, cls: "" };
            return (
              <div key={t.id} className="intel-entity">
                <div className="intel-entity-head">
                  <span className="strong">{t.name}</span>
                  {t.type && <span className="chip intel-type">{t.type}</span>}
                  <span className="spacer" />
                  <span className={`chip ${h.cls}`}>{h.label}</span>
                </div>
                {(t.firstChapter || t.lastChapter) && (
                  <div className="intel-entity-meta faint">
                    {t.firstChapter && <span>introduced {t.firstChapter}</span>}
                    {t.lastChapter && t.lastChapter !== t.firstChapter && <span>last developed {t.lastChapter}</span>}
                  </div>
                )}
                {t.description && <p className="intel-desc">{t.description}</p>}
                {t.healthReason && <p className="intel-reason"><Icon name="alert" size={11} /> {t.healthReason}</p>}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function SummaryRow({
  scene,
  expanded,
  onToggle,
  onOpenScene,
  onSummarized
}: {
  scene: { sceneId: string; title: string; chapterTitle: string; summary: string };
  expanded: boolean;
  onToggle: () => void;
  onOpenScene: (id: string) => void;
  onSummarized: () => Promise<void>;
}) {
  const has = scene.summary.trim().length > 0;
  return (
    <li className="summary-item">
      <button className="summary-head" onClick={onToggle}>
        <Icon name={expanded ? "chevronDown" : "chevronRight"} size={13} className="summary-twisty" />
        <span className="truncate grow">
          <span className="faint" style={{ fontSize: 11 }}>{scene.chapterTitle} · </span>
          <span className="strong" style={{ fontSize: 13 }}>{scene.title}</span>
        </span>
        {has ? (
          <span className="chip insight-ok" style={{ fontSize: 10 }}><Icon name="check" size={10} /> summary</span>
        ) : (
          <span className="chip" style={{ fontSize: 10 }}>not summarized</span>
        )}
      </button>
      {expanded && (
        <div className="summary-body">
          {has ? (
            <p className="summary-text">{scene.summary}</p>
          ) : (
            <p className="muted" style={{ fontSize: 12, margin: 0 }}>No summary yet.</p>
          )}
          <div className="row gap-2" style={{ marginTop: 8 }}>
            <button className="btn btn-sm btn-ghost" onClick={() => onOpenScene(scene.sceneId)}>
              <Icon name="file" size={12} /> Open scene
            </button>
            <SummarizeButtonLabeled sceneId={scene.sceneId} onDone={onSummarized} label={has ? "Regenerate" : "Generate summary"} />
          </div>
        </div>
      )}
    </li>
  );
}

function SummarizeButtonLabeled({ sceneId, onDone, label }: { sceneId: string; onDone: () => Promise<void>; label: string }) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    const choice = await pickModel({ task: "summary" });
    if (!choice) return;
    setBusy(true);
    try {
      await api.summarizeScene(sceneId, choice);
      await onDone();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Summarize failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button className="btn btn-sm btn-ghost" onClick={run} disabled={busy}>
      <Icon name={busy ? "clock" : "sparkles"} size={12} className={busy ? "spin" : ""} /> {busy ? "Summarizing…" : label}
    </button>
  );
}
